import { useState } from 'react';
import { Link, useParams } from 'react-router';
import clsx from 'clsx';
import { ProjectColorBar } from '../components/ProjectColorBar';
import { Lightbox, type LightboxPhoto } from '../components/Lightbox';
import { MOCK_PROJECT, type MockPhoto } from '../lib/mockData';

type PhotoMode = 'pm' | 'client';

/**
 * /preview/:slug/photos — dedicated Photos page with two distinct modes.
 *
 *   PM Photo Updates (default)  — curated weekly cards split into Pre-Construction
 *                                 and Construction. PM uploads from inside Conduit
 *                                 (Notes → Photo Updates tab); never from the portal.
 *   Client Photo Uploads        — simple chronological grid + upload zone for the
 *                                 client. No phase hierarchy. Date-stamped only.
 *
 * The toggle at the top is a visible mode switcher so the two streams
 * stay clearly separated.
 */
export function PreviewPhotos() {
  const { slug = '' } = useParams<{ slug: string }>();
  const project = MOCK_PROJECT;

  const [mode, setMode] = useState<PhotoMode>('pm');
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  function openLightbox(photos: MockPhoto[], index: number) {
    setLightboxPhotos(
      photos.map((p) => ({ id: p.id, url: p.url, caption: p.caption })),
    );
    setLightboxIndex(index);
  }

  return (
    <div className="min-h-screen pb-12">
      <div
        className="text-center text-xs py-1.5 px-3 num"
        style={{
          background: 'var(--color-warning-soft)',
          color: 'var(--color-warning)',
          borderBottom: '1px solid var(--color-warning)',
        }}
      >
        v2 PREVIEW · MOCK DATA · /preview/{slug}/photos
      </div>

      <header className="bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <ProjectColorBar colorTag={project.color_tag} height={12} />
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link
            to={`/preview/${slug}`}
            className="text-xs text-[var(--color-ink-2)] hover:text-[var(--color-accent)] inline-flex items-center gap-1"
          >
            ← Back to project
          </Link>
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="label-mono mb-1">Photos</p>
              <h1 className="text-3xl font-bold text-[var(--color-ink)] tracking-tight">
                {project.client_name}
              </h1>
              <p className="text-sm text-[var(--color-ink-2)] mt-1">{project.city_state}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Mode toggle — clear separator between PM updates and Client uploads */}
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)] sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-center gap-2">
          <ModeButton
            label="PM Photo Updates"
            sub={`${countPmPhotos(project)} photos`}
            active={mode === 'pm'}
            onClick={() => setMode('pm')}
          />
          <ModeButton
            label="Client Photo Uploads"
            sub={`${project.clientUploads.length} photos`}
            active={mode === 'client'}
            onClick={() => setMode('client')}
          />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {mode === 'pm' ? (
          <PmPhotoUpdates onOpen={openLightbox} />
        ) : (
          <ClientPhotoUploads onOpen={openLightbox} />
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-6 mt-12 text-xs text-[var(--color-ink-3)] text-center">
        Rough-In Advisory LLC
      </footer>

      <Lightbox
        photos={lightboxPhotos || []}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  );
}

function ModeButton({
  label,
  sub,
  active,
  onClick,
}: {
  label: string;
  sub?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'px-5 py-2.5 rounded-md text-sm font-semibold transition-all border',
        active
          ? 'text-white shadow-sm'
          : 'text-[var(--color-ink-2)] bg-[var(--color-surface)] border-[var(--color-border)] hover:bg-[var(--color-surface-2)]',
      )}
      style={
        active
          ? {
              background: 'var(--color-accent)',
              borderColor: 'var(--color-accent)',
            }
          : undefined
      }
    >
      <span className="block">{label}</span>
      {sub && (
        <span
          className={clsx(
            'block text-[10px] num font-normal mt-0.5 opacity-80',
          )}
        >
          {sub}
        </span>
      )}
    </button>
  );
}

// ============================================================
// PM Photo Updates — default view
// ============================================================
function PmPhotoUpdates({
  onOpen,
}: {
  onOpen: (photos: MockPhoto[], index: number) => void;
}) {
  const project = MOCK_PROJECT;
  return (
    <div className="flex flex-col gap-12">
      <div>
        <p className="text-sm text-[var(--color-ink-2)] leading-relaxed">
          Curated weekly photo updates from your project manager. Posted on a
          weekly cadence as construction progresses.
        </p>
      </div>

      {project.photoBlocks.map((block) => (
        <section key={block.id}>
          <h3 className="text-xs uppercase tracking-wider font-bold text-[var(--color-ink-2)] mb-4 pb-2 border-b border-[var(--color-border)]">
            {block.category === 'pre-construction' ? 'Pre-Construction' : 'Construction'}
          </h3>
          {block.weeks.length === 0 ? (
            <p className="text-sm text-[var(--color-ink-3)] italic">
              No {block.category === 'pre-construction' ? 'pre-construction' : 'construction'} photo updates yet.
            </p>
          ) : (
            <div className="flex flex-col gap-7">
              {block.weeks.map((week) => (
                <div key={week.id}>
                  <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                    <p className="text-sm font-semibold text-[var(--color-ink)]">{week.week_label}</p>
                    <span className="num text-xs text-[var(--color-ink-3)]">
                      {week.photos.length} photo{week.photos.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {week.description && (
                    <p className="text-sm text-[var(--color-ink-2)] mb-3 leading-relaxed">
                      {week.description}
                    </p>
                  )}
                  <ul className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
                    {week.photos.map((p, i) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => onOpen(week.photos, i)}
                          className="group block aspect-square overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                          title={p.caption || 'Open photo'}
                        >
                          <img
                            src={p.thumb}
                            alt={p.caption || ''}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

// ============================================================
// Client Photo Uploads — simple chronological grid + upload zone
// ============================================================
function ClientPhotoUploads({
  onOpen,
}: {
  onOpen: (photos: MockPhoto[], index: number) => void;
}) {
  const project = MOCK_PROJECT;
  // Sort newest first by uploaded_at
  const photos = project.clientUploads
    .slice()
    .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));

  return (
    <div className="flex flex-col gap-8">
      <div>
        <p className="text-sm text-[var(--color-ink-2)] leading-relaxed">
          Photos shared by you or other authorized viewers. Upload anything
          relevant — site visit notes, questions, conditions you want your
          PM to see. Photos are stamped with the upload date and ordered
          newest first.
        </p>
      </div>

      {/* Upload zone — visual only in the mockup */}
      <section>
        <h3 className="text-xs uppercase tracking-wider font-bold text-[var(--color-ink-2)] mb-3">
          Upload photos
        </h3>
        <div className="border-2 border-dashed border-[var(--color-border-strong)] rounded-md bg-[var(--color-surface)] px-6 py-10 text-center">
          <p className="text-sm font-medium text-[var(--color-ink)]">
            Drop photos here, or <span className="underline">click to choose</span>
          </p>
          <p className="text-xs text-[var(--color-ink-3)] mt-1">
            .jpg, .png, .heic, .webp · up to 50 MB each
          </p>
          <p className="text-xs text-[var(--color-ink-3)] mt-2">
            Optional caption applied to all selected photos
          </p>
        </div>
      </section>

      {/* All client uploads — flat chronological grid */}
      <section>
        <div className="flex items-baseline justify-between mb-4 pb-2 border-b border-[var(--color-border)]">
          <h3 className="text-xs uppercase tracking-wider font-bold text-[var(--color-ink-2)]">
            All uploads
          </h3>
          <span className="num text-xs text-[var(--color-ink-3)]">
            {photos.length} photo{photos.length === 1 ? '' : 's'}
          </span>
        </div>

        {photos.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-3)] italic">
            No client uploads yet. Drop one above to share it with your PM.
          </p>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
            {photos.map((p, i) => (
              <li key={p.id} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => onOpen(photos, i)}
                  className="group block aspect-square overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                  title={p.caption || formatDate(p.uploaded_at)}
                >
                  <img
                    src={p.thumb}
                    alt={p.caption || ''}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </button>
                <p className="num text-[11px] text-[var(--color-ink-2)] mt-1">
                  {formatDate(p.uploaded_at)}
                </p>
                {p.caption && (
                  <p className="text-[11px] text-[var(--color-ink-3)] truncate" title={p.caption}>
                    {p.caption}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function countPmPhotos(project: typeof MOCK_PROJECT): number {
  let n = 0;
  for (const block of project.photoBlocks) {
    for (const week of block.weeks) n += week.photos.length;
  }
  return n;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default PreviewPhotos;
